import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { 
  Send, Loader2, Megaphone, Save, Eye, 
  Building2, MapPin, DollarSign, FileText, 
  MessageSquare, ChevronDown, ChevronUp, Pencil, Tag,
  ShoppingCart, CalendarDays
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { CornerCategoryBadge } from '../../utils/cornerCategory';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SectionCard({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
          {Icon && <Icon className="w-4 h-4 text-emerald-600" />}
          {title}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
}

function FieldRow({ label, value, onChange, type = 'text', suffix = '' }) {
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-slate-500 w-40 shrink-0">{label}</label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-7 text-sm flex-1"
      />
      {suffix && <span className="text-xs text-slate-400 shrink-0">{suffix}</span>}
    </div>
  );
}

const isoToDMY = (v) => {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v);
};
const dmyToISO = (v) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(v).trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}` : v;
};

function DateFieldRow({ label, value, onChange }) {
  const [display, setDisplay] = useState(isoToDMY(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setDisplay(isoToDMY(value)); }, [value]);
  const selectedDate = (() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
  })();
  const onPick = (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    onChange(iso);
    setOpen(false);
  };
  return (
    <div className="flex items-center gap-2">
      <label className="text-xs text-slate-500 w-40 shrink-0">{label}</label>
      <div className="flex items-center gap-1 flex-1">
        <Input
          value={display}
          placeholder="jj/mm/aaaa"
          onChange={(e) => { setDisplay(e.target.value); onChange(dmyToISO(e.target.value)); }}
          className="h-7 text-sm flex-1"
          data-testid="ann-date-ouverture-input"
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="icon" className="h-7 w-7 shrink-0" data-testid="ann-date-ouverture-calendar-btn">
              <CalendarDays className="w-3.5 h-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar mode="single" selected={selectedDate} onSelect={onPick} initialFocus />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

export function AnnouncementEditorModal({ open, onClose, simulationId, token, onSuccess }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [announcementId, setAnnouncementId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategories, setUploadCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (open && simulationId) {
      loadTemplate();
      loadUploadCategories();
    }
    return () => {
      setContent(null);
      setAnnouncementId(null);
      setPreviewMode(false);
      setAttachments([]);
      setSelectedCategory('');
    };
  }, [open, simulationId]);

  const loadUploadCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcement-upload-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const cats = await res.json();
        const active = cats.filter(c => c.active);
        setUploadCategories(active);
        if (active.length > 0 && !selectedCategory) setSelectedCategory(active[0].label);
      }
    } catch (e) { console.error(e); }
  };

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/announcements/template/${simulationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setContent(await res.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors du chargement');
        onClose();
      }
    } catch {
      toast.error('Erreur réseau');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateTopField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAndSubmit = async () => {
    // Validate COMMANDE A PASSER (mandatory)
    const cap = content?.commande_a_passer || {};
    if (cap.ecran_affichage === null || cap.ecran_affichage === undefined ||
        cap.meuble_wok === null || cap.meuble_wok === undefined ||
        cap.cellule_refroidissement === null || cap.cellule_refroidissement === undefined) {
      toast.error('La section "COMMANDE A PASSER" est obligatoire. Veuillez répondre à toutes les questions.');
      return;
    }

    setSubmitting(true);
    try {
      // Create announcement if not exists
      let annId = announcementId;
      if (!annId) {
        const createRes = await fetch(`${API_URL}/api/announcements`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ simulation_id: simulationId, content })
        });
        if (!createRes.ok) {
          const err = await createRes.json();
          toast.error(err.detail || 'Erreur création');
          return;
        }
        const created = await createRes.json();
        annId = created.id;
        setAnnouncementId(annId);
      } else {
        // Update content
        await fetch(`${API_URL}/api/announcements/${annId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ content })
        });
      }

      // Submit for validation
      const submitRes = await fetch(`${API_URL}/api/announcements/${annId}/submit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (submitRes.ok) {
        toast.success('Annonce soumise pour validation ! Les administrateurs ont été notifiés.');
        onSuccess?.();
        onClose();
      } else {
        const err = await submitRes.json();
        toast.error(err.detail || 'Erreur soumission');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  const ensureAnnouncement = async () => {
    if (announcementId) return announcementId;
    const res = await fetch(`${API_URL}/api/announcements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ simulation_id: simulationId, content })
    });
    if (res.ok) {
      const data = await res.json();
      setAnnouncementId(data.id);
      return data.id;
    }
    return null;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    try {
      const annId = await ensureAnnouncement();
      if (!annId) {
        toast.error('Erreur: impossible de créer l\'annonce');
        return;
      }

      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('category', selectedCategory || 'Contrat');

      const res = await fetch(`${API_URL}/api/announcements/${annId}/attachments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setAttachments(data.attachments || []);
        toast.success(`${files.length} fichier(s) ajouté(s)`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur upload');
      }
    } catch {
      toast.error('Erreur réseau');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteAttachment = async (filename) => {
    if (!announcementId) return;
    try {
      const res = await fetch(`${API_URL}/api/announcements/${announcementId}/attachments/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAttachments(data.attachments || []);
        toast.success('Fichier supprimé');
      }
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      if (!announcementId) {
        const res = await fetch(`${API_URL}/api/announcements`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ simulation_id: simulationId, content })
        });
        if (res.ok) {
          const data = await res.json();
          setAnnouncementId(data.id);
          toast.success('Brouillon sauvegardé');
        }
      } else {
        await fetch(`${API_URL}/api/announcements/${announcementId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ content })
        });
        toast.success('Brouillon mis à jour');
      }
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="announcement-editor-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-700">
            <Megaphone className="w-5 h-5" />
            Annonce ALL — {content?.enseigne} {content?.ville}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : content ? (
          <div className="space-y-3" data-testid="announcement-editor-form">
            {/* RENSEIGNEMENTS DU MAGASIN */}
            <SectionCard title="RENSEIGNEMENTS DU MAGASIN" icon={Building2}>
              <FieldRow label="Enseigne" value={content.renseignements_magasin?.enseigne}
                onChange={(v) => { updateField('renseignements_magasin', 'enseigne', v); updateTopField('enseigne', v); }} />
              <FieldRow label="Adresse" value={content.renseignements_magasin?.adresse}
                onChange={(v) => updateField('renseignements_magasin', 'adresse', v)} />
              <FieldRow label="Code postal" value={content.renseignements_magasin?.code_postal}
                onChange={(v) => updateField('renseignements_magasin', 'code_postal', v)} />
              <FieldRow label="Ville" value={content.renseignements_magasin?.ville}
                onChange={(v) => { updateField('renseignements_magasin', 'ville', v); updateTopField('ville', v); }} />
              <FieldRow label="Surface (m²)" value={content.renseignements_magasin?.surface_m2}
                onChange={(v) => updateField('renseignements_magasin', 'surface_m2', v)} type="number" suffix="m²" />
              <FieldRow label="Concept" value={content.renseignements_magasin?.concept}
                onChange={(v) => updateField('renseignements_magasin', 'concept', v)} />
            </SectionCard>

            {/* DATA DU MAGASIN (simplifié) */}
            <SectionCard title="DATA DU MAGASIN" icon={MapPin} defaultOpen={false}>
              <FieldRow label="Age moyen zone" value={content.data_magasin?.zone_age_moyen}
                onChange={(v) => updateField('data_magasin', 'zone_age_moyen', v)} />
              <FieldRow label="CSP zone" value={content.data_magasin?.zone_csp}
                onChange={(v) => updateField('data_magasin', 'zone_csp', v)} />
              <FieldRow label="Revenu médian" value={content.data_magasin?.revenu_median}
                onChange={(v) => updateField('data_magasin', 'revenu_median', v)} type="number" suffix="€" />
            </SectionCard>

            {/* INFORMATIONS PROJET */}
            <SectionCard title="INFORMATIONS PROJET" icon={MapPin}>
              <FieldRow label="Emplacement" value={content.informations_projet?.emplacement}
                onChange={(v) => updateField('informations_projet', 'emplacement', v)} />
              <FieldRow label="Note emplacement" value={content.informations_projet?.note_emplacement}
                onChange={(v) => updateField('informations_projet', 'note_emplacement', v)} />
              <FieldRow label="Projet corner" value={content.informations_projet?.projet_corner}
                onChange={(v) => updateField('informations_projet', 'projet_corner', v)} />
              <FieldRow label="Porteur CAPEX" value={content.informations_projet?.porteur_capex}
                onChange={(v) => updateField('informations_projet', 'porteur_capex', v)} />
              <FieldRow label="Collaborateurs" value={content.informations_projet?.collaborateurs}
                onChange={(v) => updateField('informations_projet', 'collaborateurs', v)} type="number" />
              <DateFieldRow label="Date d'ouverture estimée" value={content.informations_projet?.date_ouverture_estimee}
                onChange={(v) => updateField('informations_projet', 'date_ouverture_estimee', v)} />
              <DateFieldRow label="Date de signature" value={content.informations_projet?.date_signature}
                onChange={(v) => updateField('informations_projet', 'date_signature', v)} />
            </SectionCard>

            {/* INVESTISSEMENT CAPEX */}
            <SectionCard title="INVESTISSEMENT CAPEX" icon={DollarSign}>
              <FieldRow label="Corner" value={content.investissement_capex?.corner_montant}
                onChange={(v) => updateField('investissement_capex', 'corner_montant', v)} type="number" suffix={`€ (${content.investissement_capex?.corner_investisseur})`} />
              <FieldRow label="Vitrine" value={content.investissement_capex?.vitrine_montant}
                onChange={(v) => updateField('investissement_capex', 'vitrine_montant', v)} type="number" suffix={`€ (${content.investissement_capex?.vitrine_investisseur})`} />
              <FieldRow label="Inox" value={content.investissement_capex?.inox_montant}
                onChange={(v) => updateField('investissement_capex', 'inox_montant', v)} type="number" suffix={`€ (${content.investissement_capex?.inox_investisseur})`} />
              <FieldRow label="Informatique/Caisse" value={content.investissement_capex?.informatique_caisse}
                onChange={(v) => updateField('investissement_capex', 'informatique_caisse', v)} type="number" suffix="€" />
              <FieldRow label="Total CAPEX" value={content.investissement_capex?.total_capex}
                onChange={(v) => updateField('investissement_capex', 'total_capex', v)} type="number" suffix="€" />
              <FieldRow label="CAPEX CCD" value={content.investissement_capex?.capex_ccd}
                onChange={(v) => updateField('investissement_capex', 'capex_ccd', v)} type="number" suffix="€" />
              <div className="pt-2 border-t border-slate-100">
                <label className="text-xs text-slate-500 mb-1 block">Particularité</label>
                <Textarea
                  value={content.investissement_capex?.particularite || ''}
                  onChange={(e) => updateField('investissement_capex', 'particularite', e.target.value)}
                  placeholder="Précisez les particularités du CAPEX..."
                  className="min-h-16 text-sm"
                  data-testid="capex-particularite-input"
                />
              </div>
            </SectionCard>

            {/* DÉCISION DIRECTION */}
            <SectionCard title="DÉCISION DIRECTION" icon={FileText}>
              <FieldRow label="Porteur CAPEX" value={content.decision_direction?.porteur_capex}
                onChange={(v) => updateField('decision_direction', 'porteur_capex', v)} />
              <FieldRow label="Capex total" value={content.decision_direction?.capex_total}
                onChange={(v) => updateField('decision_direction', 'capex_total', v)} type="number" suffix="€" />
              <FieldRow label="CA Prévisionnel HT" value={content.decision_direction?.ca_previsionnel_ht}
                onChange={(v) => updateField('decision_direction', 'ca_previsionnel_ht', v)} type="number" suffix="€" />
              <div className="flex items-center justify-between py-1" data-testid="ann-corner-category">
                <span className="text-sm text-slate-500">Catégorie du corner</span>
                <CornerCategoryBadge ca={content.decision_direction?.ca_previsionnel_ht} testid="ann-corner-category-badge" />
              </div>
              <FieldRow label="Commission" value={content.decision_direction?.commission}
                onChange={(v) => updateField('decision_direction', 'commission', v)} type="number" suffix="%" />
            </SectionCard>

            {/* COMMANDE A PASSER */}
            <SectionCard title="COMMANDE A PASSER" icon={ShoppingCart} defaultOpen={true}>
              <p className="text-xs text-amber-600 font-medium mb-2">* Section obligatoire pour soumettre l'annonce</p>
              {[
                { key: 'ecran_affichage', label: "Ecran d'affichage" },
                { key: 'meuble_wok', label: 'Meuble Wok' },
                { key: 'cellule_refroidissement', label: 'Cellule de refroidissement' },
              ].map(item => (
                <div key={item.key} className="flex items-center gap-3 py-1.5">
                  <span className="text-xs text-slate-500 w-40 shrink-0">{item.label}</span>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`cap-${item.key}-oui`}>
                      <input
                        type="radio"
                        name={`cap_${item.key}`}
                        checked={content.commande_a_passer?.[item.key] === true}
                        onChange={() => updateField('commande_a_passer', item.key, true)}
                        className="accent-emerald-600"
                      />
                      <span className="text-sm text-slate-700">Oui</span>
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`cap-${item.key}-non`}>
                      <input
                        type="radio"
                        name={`cap_${item.key}`}
                        checked={content.commande_a_passer?.[item.key] === false}
                        onChange={() => updateField('commande_a_passer', item.key, false)}
                        className="accent-red-500"
                      />
                      <span className="text-sm text-slate-700">Non</span>
                    </label>
                  </div>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100">
                <label className="text-xs text-slate-500 mb-1 block">Commentaires</label>
                <Textarea
                  value={content.commande_a_passer?.commentaires || ''}
                  onChange={(e) => updateField('commande_a_passer', 'commentaires', e.target.value)}
                  placeholder="Commentaires sur les commandes..."
                  className="min-h-16 text-sm"
                  data-testid="cap-commentaires-input"
                />
              </div>
            </SectionCard>

            {/* INFORMATIONS COMPLÉMENTAIRES */}
            <SectionCard title="INFORMATIONS COMPLÉMENTAIRES" icon={MessageSquare} defaultOpen={true}>
              <Textarea
                value={content.salesforce?.informations || ''}
                onChange={(e) => updateField('salesforce', 'informations', e.target.value)}
                placeholder="Ajoutez vos annotations complémentaires..."
                className="min-h-20 text-sm"
                data-testid="salesforce-input"
              />
            </SectionCard>

            {/* MESSAGE FINAL */}
            <SectionCard title="MESSAGE DE CLÔTURE" icon={MessageSquare} defaultOpen={true}>
              <Textarea
                value={content.message_final || ''}
                onChange={(e) => updateTopField('message_final', e.target.value)}
                className="min-h-24 text-sm"
                data-testid="message-final-input"
              />
            </SectionCard>

            {/* PIÈCES JOINTES */}
            <SectionCard title="PIÈCES JOINTES" icon={FileText} defaultOpen={true}>
              <div className="space-y-3">
                {/* Category selector + upload */}
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={selectedCategory}
                    onChange={e => setSelectedCategory(e.target.value)}
                    className="h-9 text-sm border border-slate-200 rounded-lg px-3 bg-white dark:bg-slate-800 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                    data-testid="upload-category-select"
                  >
                    {uploadCategories.map(c => (
                      <option key={c.id} value={c.label}>{c.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg cursor-pointer hover:bg-slate-200 transition-colors border border-dashed border-slate-300">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-600 dark:text-slate-400">
                      {uploading ? 'Upload en cours...' : 'Ajouter un PDF'}
                    </span>
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="hidden"
                      data-testid="pdf-upload-input"
                    />
                  </label>
                  {uploading && <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />}
                </div>
                {attachments.length > 0 && (
                  <div className="space-y-1">
                    {attachments.map((att) => (
                      <div key={att.filename} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded border text-xs" data-testid={`attachment-${att.filename}`}>
                        <span className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                          <FileText className="w-3 h-3 text-red-500" />
                          {att.original_name} ({(att.size / 1024).toFixed(0)} Ko)
                          {att.category && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px] font-medium">
                              <Tag className="w-2.5 h-2.5" />
                              {att.category}
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDeleteAttachment(att.filename)}
                          className="text-red-500 hover:text-red-700 text-xs px-1"
                          data-testid={`delete-attachment-${att.filename}`}
                        >
                          Supprimer
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>
          </div>
        ) : null}

        <DialogFooter className="flex gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} data-testid="cancel-announcement-btn">
            Annuler
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveDraft}
            disabled={saving || !content}
            className="gap-1"
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Brouillon
          </Button>
          <Button
            onClick={handleSaveAndSubmit}
            disabled={submitting || !content}
            className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="submit-announcement-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer pour validation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
